import { useState } from "react";
import * as React from "react";
import {
    useDataTypes,
    useCreateDataType,
    useUpdateDataType,
    useDeleteDataType,
    useTypes,
    useCreateAttribute,
    useAttributes,
    useDeleteAttribute,
    useUpdateAttribute,
    useAllTypes,
} from "@/hooks/useLocalStorage";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
    ChevronRight,
    ChevronDown,
    Folder,
    FolderOpen,
    Tag,
    Plus,
    Edit,
    Trash2,
    TreePine,
    List,
    Loader2,
    Check,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
    Type,
    Code,
    insertTypeSchema,
    insertCodeSchema,
    InsertType,
    InsertCode,
} from "../shared/schema";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { DeleteConfirmationDialog } from "@/widgets/DeleteModal";
import { AlertDialog } from "@/widgets/AlertDailog";
import { PaginationControl } from "@/widgets/renderPagination";
import { parseApiErrorMessage } from "@/hooks/parseApiErrorMessage";
import { Checkbox } from "./ui/checkbox";
import { useBreadcrumbs } from "./BreadcrumbContext";

interface HierarchicalNodeProps {
    item: Type | Code;
    itemType: "type" | "code";
    level?: number;
    parentId: string
    onCodeClick?: (code: Code) => void;
}

interface TableHierarchicalNodeProps {
    topLevelTypesData: (Type)[];
    itemType: "type" | "code";
    pageIndex: number;
    pageSize: number;
    totalCount: number;
    onPageChange: (code: Code) => void;
    setDataTypeData: (type: Type | null) => void
}

function CreateDataTypeDialog({ trigger }: { trigger: React.ReactNode }) {
    const [open, setOpen] = useState(false);
    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");
    const [reqStatus, setReqStatus] = React.useState(false);
    const [currentData, setCurrentData] = useState<(Type)[]>([]);
    const [selectedDataTypeId, setSelectedDataTypeId] = useState<string>("");

    const { data: existingDtValues, isLoading } = useDataTypes(1, undefined, undefined);
    const createTypeMutation = useCreateDataType();
    const { data: attributes, isLoading: isAttrLoading } = useAttributes(
        selectedDataTypeId
    );

    React.useEffect(() => {
        if (existingDtValues && existingDtValues.total > 0) {
            setCurrentData(existingDtValues.items)
        }
    }, [existingDtValues])


    const form = useForm<InsertType>({
        resolver: zodResolver(insertTypeSchema),
        defaultValues: {
            ExistingDataTypeLink: 0,
            Name: "",
            DataLabel: "",
            UseAllAttributes: false,
        },
    });

    const onSubmit = async (data: InsertType) => {
        console.log("data", data)
        try {
            await createTypeMutation.mutateAsync(data);
            setOpen(false);
            form.reset();
        } catch (error) {
            console.error("Error creating type:", error);
        }
    };

    const clearData = () => {
        form.reset();
        setSelectedDataTypeId("")
    }

    React.useEffect(() => {
        if (createTypeMutation.isSuccess === true) {
            setOpen(false);
            setReqStatus(createTypeMutation.isSuccess)
            setAlertMessage(`Type Added Successfully!`);
            setAlertOpen(true);
            form.reset();
            setSelectedDataTypeId("")
            createTypeMutation.reset();
        } else if (createTypeMutation.isError) {
            const raw = createTypeMutation.error as Error;
            const parsedMsg = parseApiErrorMessage(raw.message);
            setReqStatus(createTypeMutation.isSuccess)
            setOpen(false);
            setAlertMessage(parsedMsg);
            form.reset();
            setSelectedDataTypeId("")
            setAlertOpen(true);
            createTypeMutation.reset();
        }

    }, [createTypeMutation])

    return (
        <div>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>{trigger}</DialogTrigger>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Create Data Dictionary Name</DialogTitle>
                    </DialogHeader>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit, (errors) => console.log("errors", errors))} className="space-y-4">
                            <FormField
                                control={form.control}
                                name="ExistingDataTypeLink"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            View Existing Data Dictionary Repository
                                        </FormLabel>
                                        <FormControl>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        className="flex w-full justify-between items-center"
                                                    >
                                                        {field.value
                                                            ? currentData.find(
                                                                (opt) => opt.ID === field.value
                                                            )?.Name
                                                            : "Select Existing DataType"}
                                                        <ChevronRight className="ml-2 h-4 w-4 opacity-50" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent>
                                                    {currentData.map((opt) => (
                                                        <DropdownMenuItem
                                                            className="w-[450px]"
                                                            key={opt.ID}
                                                            onSelect={() => {
                                                                field.onChange(opt.ID);
                                                                setSelectedDataTypeId(String(opt.ID));
                                                                form.setValue("AttributeIds", []);
                                                            }}
                                                        >
                                                            {opt.Name}
                                                            {field.value === opt.ID && (
                                                                <Check className="ml-auto h-4 w-4" />
                                                            )}
                                                        </DropdownMenuItem>
                                                    ))}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            {selectedDataTypeId && attributes?.length! > 0 && (
                                <FormField
                                    control={form.control}
                                    name="AttributeIds"
                                    render={({ field }) => {
                                        const useAll = form.watch("UseAllAttributes");

                                        return (
                                            <FormItem>
                                                <FormLabel>Select Attributes</FormLabel>

                                                <div className="border rounded-md p-2 max-h-48 overflow-auto space-y-2">

                                                    {/* ALL */}
                                                    <div className="flex items-center space-x-2 border-b pb-2">
                                                        <input
                                                            type="checkbox"
                                                            checked={useAll}
                                                            onChange={(e) => {
                                                                form.setValue("UseAllAttributes", e.target.checked);
                                                                form.setValue("AttributeIds", []);
                                                            }}
                                                        />
                                                        <span className="text-sm font-medium">All</span>
                                                    </div>

                                                    {/* INDIVIDUAL ATTRIBUTES */}
                                                    {attributes?.map((attr) => (
                                                        <div
                                                            key={attr.ID}
                                                            className="flex items-center space-x-2"
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                disabled={useAll}
                                                                checked={field.value?.includes(attr.ID)}
                                                                onChange={(e) => {
                                                                    const updated = e.target.checked
                                                                        ? [...(field.value ?? []), attr.ID]
                                                                        : field.value?.filter(id => id !== attr.ID);

                                                                    field.onChange(updated);
                                                                }}
                                                            />
                                                            <span className="text-sm">{attr.name}</span>
                                                        </div>
                                                    ))}
                                                </div>

                                                <FormMessage />
                                            </FormItem>
                                        );
                                    }}
                                />
                            )}


                            <FormField
                                control={form.control}
                                name="Name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Name</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Enter New Data Dictionary Name" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="DataLabel"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Label</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Enter Data Label" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <div className="flex justify-end space-x-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    disabled={createTypeMutation.isPending}
                                    onClick={clearData}
                                >
                                    Clear
                                </Button>
                                <Button
                                    type="submit"
                                    className="bg-workfinity-500 hover:bg-workfinity-600"
                                    disabled={createTypeMutation.isPending}
                                >
                                    {createTypeMutation.isPending ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Creating DataType...
                                        </>
                                    ) : (
                                        "Create DataType"
                                    )}
                                </Button>
                            </div>
                        </form>
                    </Form>
                </DialogContent>
            </Dialog>
            <AlertDialog
                open={alertOpen}
                onOpenChange={setAlertOpen}
                message={alertMessage}
                status={reqStatus}
            />
        </div>
    );
}

function AddChildDialog({
    parent,
    parentType,
    trigger,
}: {
    parent: Type | Code;
    parentType: "type" | "code";
    trigger: React.ReactNode;
}) {
    const [open, setOpen] = useState(false);
    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");
    const [reqStatus, setReqStatus] = React.useState(false);
    const [currentData, setCurrentData] = useState<(Type)[]>([]);
    const [selectedDataTypeId, setSelectedDataTypeId] = useState<string>("");

    const { data: cmTypes } = useAllTypes();
    const { data: existingDtValues, isLoading } = useDataTypes(1, undefined, undefined);
    const createAttrMutation = useCreateAttribute();
    const { data: attributes, isLoading: isAttrLoading } = useAttributes(
        selectedDataTypeId
    );

    React.useEffect(() => {
        if (existingDtValues && existingDtValues.total > 0) {
            setCurrentData(existingDtValues.items)
        }
    }, [existingDtValues])

    const form = useForm<InsertCode>({
        resolver: zodResolver(insertCodeSchema),
        defaultValues: {
            ID: 0,
            name: "",
            type: 0,
            label: "",
            DropdownKind: 0,
            childAttribute: "",
            cm_values: "",
            Link: "",
        },
    });

    const typeData = form.watch("type");

    const Typesname = [
        { value: 1, label: "String" },
        { value: 2, label: "Short" },
        { value: 3, label: "Decimal" },
        { value: 4, label: "Double" },
        { value: 5, label: "Float" },
        { value: 6, label: "Integer" },
        { value: 7, label: "Boolean" },
        { value: 8, label: "Date" },
        { value: 9, label: "Object" },
        { value: 10, label: "Document" },
        { value: 11, label: "Total" },
        { value: 12, label: "Link" },
    ]

    const onSubmit = async (data: InsertCode) => {
        const { DropdownKind, childAttribute, ...rest } = data;
        console.log("DropdownKind", DropdownKind)
        console.log("childAttribute", childAttribute)
        const payload = {
            ...rest,
            DropdownKind: typeData === 9 ? selectedDataTypeId : `${DropdownKind}`,
        };
        try {
            await createAttrMutation.mutateAsync({
                Id: (parent as Type).ID,
                data: payload
            });
            setOpen(false);
            form.reset();
        } catch (error) {
            console.error("Error creating child code:", error);
        }
    };

    const clearData = () => {
        form.reset();
    }

    React.useEffect(() => {
        if (createAttrMutation.isSuccess === true) {
            setOpen(false);
            setAlertMessage(`Attribute Added to ${(parent as Type).Name} Successfully!`);
            setReqStatus(createAttrMutation.isSuccess)
            setAlertOpen(true);
            createAttrMutation.reset();
        } else if (createAttrMutation.isError) {
            const raw = createAttrMutation.error as Error;
            const parsedMsg = parseApiErrorMessage(raw.message);
            setReqStatus(createAttrMutation.isSuccess)
            setOpen(false);
            setAlertMessage(parsedMsg);
            setAlertOpen(true);
            createAttrMutation.reset();
        }

    }, [createAttrMutation])

    return (
        <div>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>{trigger}</DialogTrigger>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            Add Attribute to{" "}{(parent as Type).Name}
                        </DialogTitle>
                    </DialogHeader>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">

                            <FormField
                                control={form.control}
                                name="name"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Attribute Name</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Enter name" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="label"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Label</FormLabel>
                                        <FormControl>
                                            <Input placeholder="Enter label" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="type"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            Select Type
                                        </FormLabel>
                                        <FormControl>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        className="flex w-full justify-between items-center"
                                                    >
                                                        {field.value
                                                            ? Typesname.find(
                                                                (opt) => opt.value === field.value
                                                            )?.label
                                                            : "Select Type"}
                                                        <ChevronRight className="ml-2 h-4 w-4 opacity-50" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent>
                                                    {Typesname.map((opt) => (
                                                        <DropdownMenuItem
                                                            className="w-[450px]"
                                                            key={opt.value}
                                                            onSelect={() => field.onChange(Number(opt.value))}
                                                        >
                                                            {opt.label}
                                                            {field.value === opt.value && (
                                                                <Check className="ml-auto h-4 w-4" />
                                                            )}
                                                        </DropdownMenuItem>
                                                    ))}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="cm_values"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            Select CM Value
                                        </FormLabel>
                                        <FormControl>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        className="flex w-full justify-between items-center"
                                                    >
                                                        {field.value
                                                            ? cmTypes?.types?.find(
                                                                (opt: any) => opt.Name === field.value
                                                            )?.Name
                                                            : "Select CM Value"}
                                                        <ChevronRight className="ml-2 h-4 w-4 opacity-50" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent>
                                                    {cmTypes?.types?.map((opt: any) => (
                                                        <DropdownMenuItem
                                                            className="w-[450px]"
                                                            key={opt.value}
                                                            onSelect={() => field.onChange(opt.Name)}
                                                        >
                                                            {opt.Name}
                                                            {field.value === opt.Name && (
                                                                <Check className="ml-auto h-4 w-4" />
                                                            )}
                                                        </DropdownMenuItem>
                                                    ))}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="childAttribute"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Select Source Datatype</FormLabel>
                                        <FormControl>
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="outline" className="w-full justify-between">
                                                        {field.value || "Select Source"}
                                                        <ChevronRight className="h-4 w-4 opacity-50" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent>
                                                    {currentData.map((opt) => (
                                                        <DropdownMenuItem
                                                            key={opt.ID}
                                                            onSelect={() => {
                                                                field.onChange(opt.Name);
                                                                setSelectedDataTypeId(JSON.stringify(opt.ID));
                                                            }}
                                                        >
                                                            {opt.Name}
                                                        </DropdownMenuItem>
                                                    ))}
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </FormControl>
                                    </FormItem>
                                )}
                            />

                            {typeData !== 9 &&
                                <FormField
                                    control={form.control}
                                    name="DropdownKind"
                                    render={({ field }) => {
                                        const selectedAttribute = attributes?.find(
                                            (attr) => attr.ID === field.value
                                        );

                                        return (
                                            <FormItem>
                                                <FormLabel>Select Source Attribute</FormLabel>
                                                <FormControl>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger asChild>
                                                            <Button
                                                                variant="outline"
                                                                disabled={!attributes || isAttrLoading}
                                                                className="w-full justify-between"
                                                            >
                                                                {selectedAttribute?.name || "Select Attribute"}
                                                                <ChevronRight className="h-4 w-4 opacity-50" />
                                                            </Button>
                                                        </DropdownMenuTrigger>

                                                        <DropdownMenuContent className="w-full">
                                                            {attributes?.map((opt) => (
                                                                <DropdownMenuItem
                                                                    key={opt.ID}
                                                                    onSelect={() => field.onChange(opt.ID)}
                                                                >
                                                                    {opt.name}
                                                                </DropdownMenuItem>
                                                            ))}
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </FormControl>
                                            </FormItem>
                                        );
                                    }}
                                />
                            }

                            {typeData === 12 &&
                                <FormField
                                    control={form.control}
                                    name="Link"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Label</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Enter Link" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                            }


                            <div className="flex justify-end space-x-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={clearData}
                                    disabled={createAttrMutation.isPending}
                                >
                                    Clear
                                </Button>
                                <Button
                                    type="submit"
                                    className="bg-workfinity-500 hover:bg-workfinity-600"
                                    disabled={createAttrMutation.isPending}
                                >
                                    {createAttrMutation.isPending ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Adding Attribute...
                                        </>
                                    ) : (
                                        "Add Attribute"
                                    )}
                                </Button>
                            </div>
                        </form>
                    </Form>
                </DialogContent>
            </Dialog>
            <AlertDialog
                open={alertOpen}
                onOpenChange={setAlertOpen}
                message={alertMessage}
                status={reqStatus}
            />
        </div>
    );
}

function EditItemDialog({
    item,
    itemType,
    parentCode,
    trigger,
}: {
    item: Type | Code;
    itemType: "type" | "code";
    parentCode: string;
    trigger: React.ReactNode;
}) {
    const [open, setOpen] = useState(false);
    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");
    const [reqStatus, setReqStatus] = React.useState(false);

    const [currentData, setCurrentData] = useState<(Type)[]>([]);
    const [cmTypes, setCmTypes] = useState<(any)[]>([]);
    const [selectedDataTypeId, setSelectedDataTypeId] = useState<string>("");

    const { data: cmValues } = useTypes("TYPES", 1);
    const { data: existingDtValues, isLoading } = useDataTypes(1, undefined, undefined);
    const { data: attributes, isLoading: isAttrLoading } = useAttributes(
        selectedDataTypeId
    );

    const updateTypeMutation = useUpdateDataType();
    const updateAttributeMutation = useUpdateAttribute();

    React.useEffect(() => {
        if (existingDtValues && existingDtValues.total > 0) {
            setCurrentData(existingDtValues.items)
        }
    }, [existingDtValues])

    React.useEffect(() => {
        if (cmValues && cmValues.length > 0) {
            setCmTypes(cmValues)
        }
    }, [cmValues])

    const isType = itemType === "type";
    const typeItem = item as Type;
    const codeItem = item as Code;

    const typeForm = useForm<InsertType>({
        resolver: zodResolver(insertTypeSchema),
        defaultValues: {
            ExistingDataTypeLink: 0,
            Name: isType ? typeItem.Name : "",
            DataLabel: isType ? typeItem.DataLabel : "",
            UseAllAttributes: false,
            AttributeIds: [],
        },
    });
    // console.log("typeItem", typeItem)
    const codeForm = useForm<InsertCode>({
        resolver: zodResolver(insertCodeSchema),
        defaultValues: {
            ID: !isType ? codeItem.ID : 0,
            name: !isType ? codeItem.name : "",
            type: !isType ? Number(codeItem.type) : 0,
            label: !isType ? codeItem.label : "",
            cm_values: !isType ? codeItem.cm_values : "",
            DropdownKind: 0,
            childAttribute: "",
            Link: "",
        },
    });

    const typeData = codeForm.watch("type");

    React.useEffect(() => {
        if (!isType && codeItem) {
            const [parent, child] = (codeItem.DropdownKind || "").split("_");

            codeForm.reset({
                ID: codeItem.ID || 0,
                name: codeItem.name || "",
                type: Number(codeItem.type) || 1,
                label: codeItem.label || "",
                cm_values: codeItem.cm_values || "",
                DropdownKind: codeItem.DropdownKind || 0,
                childAttribute: codeItem.childAttribute || "",
                Link: codeItem.Link || "",
            });

            const matched = currentData.find((d) => d.Name === parent);
            if (matched) {
                setSelectedDataTypeId(matched?.ID);
            }
        }
    }, [codeItem, isType, currentData]);

    React.useEffect(() => {
        if (isType && typeItem) {
            typeForm.reset({
                ExistingDataTypeLink: 0,
                Name: typeItem.Name || "",
                DataLabel: typeItem.DataLabel || "",
                UseAllAttributes: false,
                AttributeIds: [],
            });
        }
    }, [isType, typeItem, typeForm]);

    const Typesname = [
        { value: 1, label: "String" },
        { value: 2, label: "Short" },
        { value: 3, label: "Decimal" },
        { value: 4, label: "Double" },
        { value: 5, label: "Float" },
        { value: 6, label: "Integer" },
        { value: 7, label: "Boolean" },
        { value: 8, label: "Date" },
        { value: 9, label: "Object" },
        { value: 10, label: "Document" },
        { value: 11, label: "Total" },
        { value: 12, label: "Link" },
    ]

    const onSubmitType = async (data: InsertType) => {
        try {
            await updateTypeMutation.mutateAsync({
                key: typeItem.ID!,
                updates: typeItem.hasAttributes ? {
                    ExistingDataTypeLink: data.ExistingDataTypeLink,
                    UseAllAttributes: data.UseAllAttributes,
                    AttributeIds: data.UseAllAttributes ? [] : data.AttributeIds,
                } : data,
            });
            setOpen(false);
        } catch (error) {
            console.error("Error updating type:", error);
        }
    };


    const onSubmitCode = async (
        data: InsertCode
    ) => {
        const { DropdownKind, childAttribute, ...rest } = data;
        const payload = {
            ...rest,
            DropdownKind: typeData === 9 ? selectedDataTypeId : `${DropdownKind}`,
        };

        await updateAttributeMutation.mutateAsync({
            type: codeItem.name,
            code: parentCode,
            updates: payload
        });
        setOpen(false);
    };

    const clearData = () => {
        typeForm.reset();
        codeForm.reset();
    }

    React.useEffect(() => {
        if (updateTypeMutation.isSuccess === true) {
            setOpen(false);
            setReqStatus(updateTypeMutation.isSuccess)
            setAlertMessage(`DataType Edited Successfully!`);
            setAlertOpen(true);
            clearData()
            updateTypeMutation.reset();
        } else if (updateTypeMutation.isError) {
            const raw = updateTypeMutation.error as Error;
            const parsedMsg = parseApiErrorMessage(raw.message);
            setReqStatus(updateTypeMutation.isSuccess)
            setOpen(false);
            setAlertMessage(parsedMsg);
            setAlertOpen(true);
            clearData()
            updateTypeMutation.reset();
        }
    }, [updateTypeMutation])

    React.useEffect(() => {
        if (updateAttributeMutation.isSuccess === true) {
            setOpen(false);
            setReqStatus(updateAttributeMutation.isSuccess)
            setAlertMessage(`Attribute Edited Successfully!`);
            setAlertOpen(true);
            clearData()
            updateAttributeMutation.reset();
        } else if (updateAttributeMutation.isError) {
            const raw = updateAttributeMutation.error as Error;
            const parsedMsg = parseApiErrorMessage(raw.message);
            setReqStatus(updateAttributeMutation.isSuccess)
            setOpen(false);
            setAlertMessage(parsedMsg);
            setAlertOpen(true);
            clearData()
            updateAttributeMutation.reset();
        }
    }, [updateAttributeMutation])

    return (
        <div>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>{trigger}</DialogTrigger>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            Edit {isType ? "DataType" : "Attribute"}:{" "}
                            {isType ? typeItem.Name : codeItem.name}
                        </DialogTitle>
                    </DialogHeader>
                    {isType ? (
                        <Form {...typeForm}>
                            <form
                                onSubmit={typeForm.handleSubmit(onSubmitType, (errors) => console.log("errors", errors))}
                                className="space-y-4"
                            >

                                <FormField
                                    control={typeForm.control}
                                    name="ExistingDataTypeLink"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Select Existing DataType</FormLabel>
                                            <FormControl>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            className="w-full justify-between"
                                                        >
                                                            {field.value
                                                                ? currentData.find((d) => d.ID === field.value)?.Name
                                                                : "Select DataType"}
                                                            <ChevronRight className="ml-2 h-4 w-4 opacity-50" />
                                                        </Button>
                                                    </DropdownMenuTrigger>

                                                    <DropdownMenuContent>
                                                        {currentData.map((opt) => (
                                                            <DropdownMenuItem
                                                                key={opt.ID}
                                                                onSelect={() => {
                                                                    field.onChange(opt.ID);
                                                                    setSelectedDataTypeId(String(opt.ID));

                                                                    // RESET ATTRIBUTE SELECTION
                                                                    typeForm.setValue("AttributeIds", []);
                                                                    typeForm.setValue("UseAllAttributes", false);
                                                                }}
                                                            >
                                                                {opt.Name}
                                                                {field.value === opt.ID && (
                                                                    <Check className="ml-auto h-4 w-4" />
                                                                )}
                                                            </DropdownMenuItem>
                                                        ))}
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />

                                {selectedDataTypeId && attributes?.length! > 0 && (
                                    <FormField
                                        control={typeForm.control}
                                        name="AttributeIds"
                                        render={({ field }) => {
                                            const useAll = typeForm.watch("UseAllAttributes");

                                            return (
                                                <FormItem>
                                                    <FormLabel>Select Attributes</FormLabel>

                                                    <div className="border rounded-md p-2 max-h-48 overflow-auto space-y-2">

                                                        {/* ALL */}
                                                        <div className="flex items-center space-x-2 border-b pb-2">
                                                            <input
                                                                type="checkbox"
                                                                checked={useAll}
                                                                onChange={(e) => {
                                                                    typeForm.setValue("UseAllAttributes", e.target.checked);
                                                                    typeForm.setValue("AttributeIds", []);
                                                                }}
                                                            />
                                                            <span className="text-sm font-medium">All</span>
                                                        </div>

                                                        {/* ATTRIBUTES */}
                                                        {attributes?.map((attr) => (
                                                            <div key={attr.ID} className="flex items-center space-x-2">
                                                                <input
                                                                    type="checkbox"
                                                                    disabled={useAll}
                                                                    checked={field.value?.includes(attr.ID)}
                                                                    onChange={(e) => {
                                                                        const updated = e.target.checked
                                                                            ? [...(field.value ?? []), attr.ID]
                                                                            : field.value?.filter(id => id !== attr.ID);

                                                                        field.onChange(updated);
                                                                    }}
                                                                />
                                                                <span className="text-sm">{attr.name}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </FormItem>
                                            );
                                        }}
                                    />
                                )}

                                <FormField
                                    control={typeForm.control}
                                    name="Name"
                                    disabled={typeItem.hasAttributes}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Name</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Enter New Data Dictionary Name" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={typeForm.control}
                                    name="DataLabel"
                                    disabled={typeItem.hasAttributes}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Label</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Enter Data Label" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <div className="flex justify-end space-x-2">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        disabled={updateTypeMutation.isPending}
                                        onClick={clearData}
                                    >
                                        Clear
                                    </Button>
                                    <Button
                                        type="submit"
                                        disabled={updateTypeMutation.isPending}
                                        className="bg-workfinity-500 hover:bg-workfinity-600"
                                    >
                                        {updateTypeMutation.isPending ? (
                                            <>
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                Updating DataType...
                                            </>
                                        ) : (
                                            "Update DataType"
                                        )}
                                    </Button>
                                </div>
                            </form>
                        </Form>
                    ) : (
                        <Form {...codeForm}>
                            <form
                                onSubmit={codeForm.handleSubmit(onSubmitCode, (errors) => console.log("errorscode", errors))}
                                className="space-y-4"
                            >
                                <FormField
                                    control={codeForm.control}
                                    name="name"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Attribute Name</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Enter name" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={codeForm.control}
                                    name="label"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Label</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Enter label" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={codeForm.control}
                                    name="type"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>
                                                Select Type
                                            </FormLabel>
                                            <FormControl>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            className="flex w-full justify-between items-center"
                                                        >
                                                            {field.value
                                                                ? Typesname.find(
                                                                    (opt) => opt.value === field.value
                                                                )?.label
                                                                : "Select Type"}
                                                            <ChevronRight className="ml-2 h-4 w-4 opacity-50" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent>
                                                        {Typesname.map((opt) => (
                                                            <DropdownMenuItem
                                                                className="w-[450px]"
                                                                key={opt.value}
                                                                onSelect={() => field.onChange(Number(opt.value))}
                                                            >
                                                                {opt.label}
                                                                {field.value === opt.value && (
                                                                    <Check className="ml-auto h-4 w-4" />
                                                                )}
                                                            </DropdownMenuItem>
                                                        ))}
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={codeForm.control}
                                    name="cm_values"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>
                                                Select CM Value
                                            </FormLabel>
                                            <FormControl>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            className="flex w-full justify-between items-center"
                                                        >
                                                            {
                                                                field.value || "Select CM Value"
                                                                // field.value
                                                                //     ? cmTypes.find(
                                                                //         (opt) => opt.name === field.value
                                                                //     )?.name
                                                                //     : "Select CM Value"
                                                            }
                                                            <ChevronRight className="ml-2 h-4 w-4 opacity-50" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent>
                                                        {cmTypes.map((opt) => (
                                                            <DropdownMenuItem
                                                                className="w-[450px]"
                                                                key={opt.value}
                                                                onSelect={() => field.onChange(opt.name)}
                                                            >
                                                                {opt.name}
                                                                {field.value === opt.name && (
                                                                    <Check className="ml-auto h-4 w-4" />
                                                                )}
                                                            </DropdownMenuItem>
                                                        ))}
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={codeForm.control}
                                    name="childAttribute"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Select Source Datatype</FormLabel>
                                            <FormControl>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="outline" className="w-full justify-between">
                                                            {field.value || "Select Source"}
                                                            <ChevronRight className="h-4 w-4 opacity-50" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent>
                                                        {currentData.map((opt) => (
                                                            <DropdownMenuItem
                                                                key={opt.ID}
                                                                onSelect={() => {
                                                                    field.onChange(opt.Name);
                                                                    setSelectedDataTypeId(JSON.stringify(opt.ID));
                                                                }}
                                                            >
                                                                {opt.Name}
                                                            </DropdownMenuItem>
                                                        ))}
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />

                                {typeData !== 9 &&
                                    <FormField
                                        control={codeForm.control}
                                        name="DropdownKind"
                                        render={({ field }) => {
                                            const selectedAttribute = attributes?.find(
                                                (attr) => attr.ID === field.value
                                            );

                                            return (
                                                <FormItem>
                                                    <FormLabel>Select Source Attribute</FormLabel>
                                                    <FormControl>
                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button
                                                                    variant="outline"
                                                                    disabled={!attributes || isAttrLoading}
                                                                    className="w-full justify-between"
                                                                >
                                                                    {selectedAttribute?.name || field.value || "Select Attribute"}
                                                                    <ChevronRight className="h-4 w-4 opacity-50" />
                                                                </Button>
                                                            </DropdownMenuTrigger>

                                                            <DropdownMenuContent className="w-full">
                                                                {attributes?.map((opt) => (
                                                                    <DropdownMenuItem
                                                                        key={opt.ID}
                                                                        onSelect={() => field.onChange(opt.ID)}
                                                                    >
                                                                        {opt.name}
                                                                    </DropdownMenuItem>
                                                                ))}
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    </FormControl>
                                                </FormItem>
                                            );
                                        }}
                                    />
                                }

                                {typeData === 12 &&
                                    <FormField
                                        control={codeForm.control}
                                        name="Link"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Link</FormLabel>
                                                <FormControl>
                                                    <Input placeholder="Enter Link" {...field} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                }


                                <div className="flex justify-end space-x-2">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        disabled={updateAttributeMutation.isPending}
                                        onClick={clearData}
                                    >
                                        Clear
                                    </Button>
                                    <Button
                                        type="submit"
                                        className="bg-workfinity-500 hover:bg-workfinity-600"
                                        disabled={updateAttributeMutation.isPending}
                                    >
                                        {updateAttributeMutation.isPending ? (
                                            <>
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                Updating Attribute...
                                            </>
                                        ) : (
                                            "Update Attribute"
                                        )}
                                    </Button>
                                </div>
                            </form>
                        </Form>
                    )}
                </DialogContent>
            </Dialog>
            <AlertDialog
                open={alertOpen}
                onOpenChange={setAlertOpen}
                message={alertMessage}
                status={reqStatus}
            />
        </div>
    );
}


function TableHierarchicalNode({ topLevelTypesData, itemType, pageIndex, pageSize, totalCount, onPageChange, setDataTypeData }: TableHierarchicalNodeProps) {
    const [currentData, setCurrentData] = useState<(Type | Code)[]>([]);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<Type | Code | null>(null);

    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");
    const [reqStatus, setReqStatus] = React.useState(false);

    const [parentCode, setParentCode] = useState<string>("");
    const [viewMode, setViewMode] = useState<"DataTypes" | "Attributes">("DataTypes");

    const { data: attributes, isLoading } = useAttributes(parentCode);
    const deleteTypeMutation = useDeleteDataType();
    const deleteAttributeMutation = useDeleteAttribute();

    React.useEffect(() => {
        if (topLevelTypesData) {
            setCurrentData(topLevelTypesData);
            setViewMode("DataTypes")
        }
    }, [topLevelTypesData]);

    function formatDate(dateString: string): string {
        const date = new Date(dateString);

        if (isNaN(date.getTime())) {
            return "";
        }
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();

        return `${day}-${month}-${year}`;
    }


    function handleView(item: Type) {
        if (!item.ID) return;

        setParentCode(JSON.stringify(item.ID));
        setViewMode("Attributes");
        setDataTypeData(item)

        if (attributes) {
            setCurrentData(attributes);
        }
    }

    React.useEffect(() => {
        if (!isLoading && attributes) {
            if (viewMode === "Attributes") {
                setCurrentData(attributes);
            }
        }
    }, [attributes, isLoading, parentCode, viewMode]);

    function handleDeleteClick(e: React.MouseEvent, item: Type | Code) {
        e.stopPropagation();
        setItemToDelete(item);
        setDeleteDialogOpen(true);
    }

    function handleConfirmDelete() {
        if (!itemToDelete) return;

        if (itemToDelete.Name) {
            deleteTypeMutation.mutate({
                key: itemToDelete?.ID,
            });
        } else {
            deleteAttributeMutation.mutate({
                type: parentCode,
                code: itemToDelete.name,
            });
        }

    }

    const TYPE_LABEL_MAP: Record<number, string> = {
        1: "String",
        2: "Short",
        3: "Decimal",
        4: "Double",
        5: "Float",
        6: "Integer",
        7: "Boolean",
        8: "Date",
        9: "Object",
        10: "Document",
        11: "Total",
        12: "Link",
    };

    React.useEffect(() => {
        if (deleteTypeMutation.isSuccess === true) {
            setDeleteDialogOpen(false);
            setReqStatus(deleteTypeMutation.isSuccess)
            setAlertMessage(`DataType deleted Successfully!`);
            setItemToDelete(null);
            setAlertOpen(true);
            deleteTypeMutation.reset();
        } else if (deleteTypeMutation.isError) {
            const raw = deleteTypeMutation.error as Error;
            const parsedMsg = parseApiErrorMessage(raw.message);
            setReqStatus(deleteTypeMutation.isSuccess)
            setDeleteDialogOpen(false);
            setAlertMessage(parsedMsg);
            setAlertOpen(true);
            deleteTypeMutation.reset();
        }
    }, [deleteTypeMutation])

    React.useEffect(() => {
        if (deleteAttributeMutation.isSuccess === true) {
            setDeleteDialogOpen(false);
            setReqStatus(deleteAttributeMutation.isSuccess)
            setAlertMessage(`Attribute deleted Successfully!`);
            setItemToDelete(null);
            setAlertOpen(true);
            deleteAttributeMutation.reset();
        } else if (deleteAttributeMutation.isError) {
            const raw = deleteAttributeMutation.error as Error;
            const parsedMsg = parseApiErrorMessage(raw.message);
            setReqStatus(deleteAttributeMutation.isSuccess)
            setDeleteDialogOpen(false);
            setAlertMessage(parsedMsg);
            setAlertOpen(true);
            deleteAttributeMutation.reset();
        }
    }, [deleteTypeMutation])
    // console.log("currentData", currentData)
    return (
        <div >
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-black pl-2">
                    {viewMode === "DataTypes" ? "Data Types" : "Attributes"}
                </h2>

                {viewMode === "Attributes" && (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                            setViewMode("DataTypes");
                            setCurrentData(topLevelTypesData || []);
                            setDataTypeData(null)
                        }}
                    >
                        ← Back
                    </Button>
                )}
            </div>
            <div className="border bg-white border-gray-200 rounded-lg p-2">
                <Table >
                    <TableHeader>
                        <TableRow >
                            {viewMode === "DataTypes" ? (
                                <>
                                    <TableHead>Data Type</TableHead>
                                    <TableHead>Data Label</TableHead>
                                    <TableHead>Date & Time</TableHead>
                                    <TableHead>Application ID</TableHead>
                                    <TableHead>Actions</TableHead>
                                </>
                            ) : (
                                <>
                                    <TableHead>Attribute Name</TableHead>
                                    <TableHead>Attribute label</TableHead>
                                    <TableHead>Type</TableHead>
                                    <TableHead>CM Values</TableHead>
                                    <TableHead>Source</TableHead>
                                    <TableHead>Actions</TableHead>
                                </>
                            )}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {currentData?.map((data) => (
                            <TableRow >
                                <TableCell className="font-mono">
                                    {(data.hasAttributes) ?
                                        <Button
                                            size="sm"
                                            variant={"link"}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleView(data);
                                            }}
                                        >
                                            {viewMode === "DataTypes" ? data.Name : data.name}
                                        </Button>
                                        :
                                        <span className="pl-3">{viewMode === "DataTypes" ? data.Name : data.name}</span>

                                    }
                                </TableCell>
                                <TableCell className="font-mono font-medium">
                                    {viewMode === "DataTypes" ? data.DataLabel : data.label}
                                </TableCell>

                                {
                                    viewMode === "DataTypes" &&
                                    <TableCell>
                                        {viewMode === "DataTypes" && new Date(data.CreatedDateTime).toLocaleString()}
                                    </TableCell>
                                }

                                {viewMode === "Attributes" && (
                                    <TableCell>
                                        {TYPE_LABEL_MAP[data.type] ?? "-"}
                                    </TableCell>
                                )}

                                {
                                    viewMode === "Attributes" &&
                                    <TableCell>
                                        {data.cm_values}
                                    </TableCell>
                                }

                                {
                                    viewMode === "Attributes" &&
                                    <TableCell>
                                        {data.childAttribute ?
                                            `${data.childAttribute}_${data.DropdownKind}` :
                                            data.DropdownKind
                                        }
                                        {" "}
                                        {
                                            data.Link && data.Link
                                        }
                                    </TableCell>
                                }

                                {
                                    viewMode === "DataTypes" &&
                                    <TableCell>
                                        {data?.ApplicationID}
                                    </TableCell>
                                }

                                <TableCell>
                                    <div className="flex items-center space-x-2">
                                        {data.Name ? (
                                            <AddChildDialog
                                                parent={data}
                                                parentType={data.Name ? "type" : "code"}
                                                trigger={
                                                    <button
                                                        onClick={(e) => e.stopPropagation()}
                                                        title="Add Attribute"
                                                        className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded hover:bg-gray-100"
                                                    >
                                                        <Plus size={20} strokeWidth={2.5} />
                                                    </button>
                                                }
                                            />
                                        ) : (
                                            <div className="w-9 h-9 md:w-10 md:h-10" />
                                        )}

                                        <EditItemDialog
                                            item={data}
                                            itemType={data.Name ? "type" : "code"}
                                            parentCode={parentCode ?? ""}
                                            trigger={
                                                <button
                                                    onClick={(e) => e.stopPropagation()}
                                                    title={
                                                        data?.type === "TYPES"
                                                            ? "Edit this DataType"
                                                            : "Edit this Attribute"
                                                    }
                                                    className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded hover:bg-gray-100"
                                                >
                                                    <Edit size={20} strokeWidth={2.5} />
                                                </button>
                                            }
                                        />

                                        <button
                                            onClick={(e) => handleDeleteClick(e, data)}
                                            title={
                                                data?.Name
                                                    ? "Delete this DataType"
                                                    : "Delete this Attribute"
                                            }
                                            className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded hover:bg-gray-100 text-red-600"
                                        >
                                            <Trash2 size={20} strokeWidth={2.5} />
                                        </button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {
                    viewMode === "DataTypes" &&
                    <PaginationControl
                        pageIndex={pageIndex}
                        pageSize={pageSize}
                        totalCount={totalCount}
                        onPageChange={onPageChange}
                    />
                }

            </div>

            <DeleteConfirmationDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                onConfirm={handleConfirmDelete}
                isPending={itemToDelete?.Name ? deleteTypeMutation.isPending : deleteAttributeMutation.isPending}
                itemName={itemToDelete?.Name ? (itemToDelete as Type).Name : (itemToDelete as Code)?.Name}
                itemType={itemToDelete?.Name ? "DataType" : "Attribute"}
            />

            <AlertDialog
                open={alertOpen}
                onOpenChange={setAlertOpen}
                message={alertMessage}
                status={reqStatus}
            />

        </div >
    );
}

function HierarchicalNode({ item, itemType, level = 0, parentId = "", onCodeClick }: HierarchicalNodeProps) {

    const [expanded, setExpanded] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<Type | Code | null>(null);

    const [alertOpen, setAlertOpen] = React.useState(false);
    const [alertMessage, setAlertMessage] = React.useState("");
    const [reqStatus, setReqStatus] = React.useState(false);

    const [children, setChildren] = useState<Type[]>([]);
    const [loading, setLoading] = useState(false);
    const [parentCode, setParentCode] = useState("");

    const { data: attributes, isLoading } = useAttributes(parentCode);
    const deleteTypeMutation = useDeleteDataType();
    const deleteAttributeMutation = useDeleteAttribute();

    const handleToggleExpand = (type: Type) => {
        if (type?.hasAttributes) {
            if (!expanded) {
                if (children.length === 0) {
                    setParentCode(type.ID ?? "");
                    setLoading(true);
                }
            }
            setExpanded(!expanded);
        }

    };

    React.useEffect(() => {
        if (!isLoading && attributes && attributes.length > 0) {
            setChildren(attributes);
            setLoading(false)
        }
    }, [attributes, isLoading, parentCode]);

    const getIcon = () => {
        if (itemType === "type") {
            return expanded ? (
                <FolderOpen className="h-4 w-4 text-blue-600" />
            ) : (
                <Folder className="h-4 w-4 text-blue-600" />
            );
        }
        else if (item.Name) {
            return expanded ? (
                <FolderOpen className="h-4 w-4 text-amber-600" />
            ) : (
                <Folder className="h-4 w-4 text-amber-600" />
            );
        } else {
            return <Tag className="h-4 w-4 text-red-600" />;
        }
    };

    const getIconBackground = () => {
        if (itemType === "type") {
            return "bg-blue-100";
        } else if (item.Name) {
            return "bg-blue-100";
        } else {
            return "bg-green-100";
        }
    };

    function handleDeleteClick(e: React.MouseEvent, item: Type | Code) {
        e.stopPropagation();
        setItemToDelete(item);
        setDeleteDialogOpen(true);
    }

    function handleConfirmDelete() {
        if (!itemToDelete) return;
        console.log("parent", parentCode)
        if (itemToDelete.Name) {
            deleteTypeMutation.mutate({
                key: itemToDelete?.ID!,
            });
        } else {
            deleteAttributeMutation.mutate({
                type: parentCode || parentId,
                code: itemToDelete.name,
            });
        }

    }

    React.useEffect(() => {
        if (deleteTypeMutation.isSuccess === true) {
            setDeleteDialogOpen(false);
            setReqStatus(deleteTypeMutation.isSuccess)
            setAlertMessage(`DataType deleted Successfully!`);
            setItemToDelete(null);
            setAlertOpen(true);
            deleteTypeMutation.reset();
        } else if (deleteTypeMutation.isError) {
            const raw = deleteTypeMutation.error as Error;
            const parsedMsg = parseApiErrorMessage(raw.message);
            setReqStatus(deleteTypeMutation.isSuccess)
            setDeleteDialogOpen(false);
            setAlertMessage(parsedMsg);
            setAlertOpen(true);
            deleteTypeMutation.reset();
        }
    }, [deleteTypeMutation])

    React.useEffect(() => {
        if (deleteAttributeMutation.isSuccess === true) {
            setDeleteDialogOpen(false);
            setReqStatus(deleteAttributeMutation.isSuccess)
            setAlertMessage(`Attribute deleted Successfully!`);
            setItemToDelete(null);
            setAlertOpen(true);
            deleteAttributeMutation.reset();
        } else if (deleteAttributeMutation.isError) {
            const raw = deleteAttributeMutation.error as Error;
            const parsedMsg = parseApiErrorMessage(raw.message);
            setReqStatus(deleteAttributeMutation.isSuccess)
            setDeleteDialogOpen(false);
            setAlertMessage(parsedMsg);
            setAlertOpen(true);
            deleteAttributeMutation.reset();
        }
    }, [deleteAttributeMutation])


    return (
        <div className="border bg-white border-gray-200 rounded-lg">
            <div
                className="flex items-center space-x-2 md:space-x-3 p-2 md:p-3 hover:bg-gray-50 cursor-pointer"
                style={{ paddingLeft: `${8 + level * 16}px` }}
            >
                {(item?.hasAttributes) && (
                    <button
                        className="p-1 hover:bg-gray-200 rounded flex-shrink-0"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleToggleExpand(item);
                        }}
                    >
                        {loading ? (
                            <span className="h-4 w-4 animate-spin">⏳</span>
                        ) : expanded ? (
                            <ChevronDown className="h-4 w-4" />
                        ) : (
                            <ChevronRight className="h-4 w-4" />
                        )}
                    </button>
                )}

                {!(item?.hasAttributes) && <div className="w-6" />}

                <div
                    className={cn(
                        "w-8 h-8 rounded-lg flex items-center justify-center",
                        getIconBackground()
                    )}
                >
                    {getIcon()}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-2">
                        <span className="font-medium text-gray-900 truncate text-sm md:text-base">
                            {itemType === "type" ? (item as Type).Name : (item as Code).name}
                        </span>
                        <span className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-1 rounded flex-shrink-0">
                            {itemType === "type" ? (item as Type).ID : (item as Code).label}
                        </span>
                    </div>
                    {item.Usability && (
                        <div className="text-xs md:text-sm text-gray-500 truncate mt-1">
                            {item.Usability}
                        </div>
                    )}
                </div>

                <div className="flex items-center space-x-1 md:space-x-2 flex-shrink-0">

                    {item?.Name &&
                        <AddChildDialog
                            parent={item}
                            parentType={itemType}
                            trigger={
                                <button
                                    onClick={(e) => e.stopPropagation()}
                                    title="Add Attribute"
                                    className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded hover:bg-gray-100"
                                >
                                    <Plus size={20} strokeWidth={2.5} />
                                </button>
                            }
                        />
                    }
                    {/* Edit Button */}
                    <EditItemDialog
                        item={item}
                        itemType={itemType}
                        parentCode={parentCode || parentId}
                        trigger={
                            <button
                                onClick={(e) => e.stopPropagation()}
                                title={
                                    item?.type === "TYPES"
                                        ? "Edit this DataType"
                                        : "Edit this Attribute"
                                }
                                className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded hover:bg-gray-100"
                            >
                                <Edit size={20} strokeWidth={2.5} />
                            </button>
                        }
                    />

                    <button
                        onClick={(e) => handleDeleteClick(e, item)}
                        title={
                            item?.Name
                                ? "Delete this DataType"
                                : "Delete this Attribute"
                        }
                        className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded hover:bg-gray-100 text-red-600"
                    >
                        <Trash2 size={20} strokeWidth={2.5} />
                    </button>


                </div>
            </div>

            {expanded && (children.length > 0) && (
                <div className="border-t border-gray-200 bg-gray-50">
                    <div className="py-2 space-y-1">
                        {children.map((childCode) => (
                            <HierarchicalNode
                                key={`code-${childCode.name}-${childCode.label}`}
                                item={childCode}
                                itemType="code"
                                level={level + 1}
                                parentId={item?.ID || parentCode}
                                onCodeClick={onCodeClick}
                            />
                        ))}
                    </div>
                </div>
            )}


            <DeleteConfirmationDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                onConfirm={handleConfirmDelete}
                isPending={itemToDelete?.Name ? deleteTypeMutation.isPending : deleteAttributeMutation.isPending}
                itemName={itemToDelete?.Name ? (itemToDelete as Type).name : (itemToDelete as Code)?.name}
                itemType={itemToDelete?.Name ? "DataType" : "Attribute"}
            />

            <AlertDialog
                open={alertOpen}
                onOpenChange={setAlertOpen}
                message={alertMessage}
                status={reqStatus}
            />
        </div>
    );
}



export function DataTypeManager() {

    const testdata = [
        {
            "Name": "testA",
            "DataLabel": "Atest",
            "Version": 0,
            "XsdSchema": "{\"$schema\": \"http://json-schema.org/draft-07/schema#\"}",
            "IsActive": true,
            "ApplicationScope": null,
            "ApplicationID": 30,
            "ID": 115,
            "CreatedDateTime": "2025-09-11T23:32:18.470000",
            "hasAttributes": false
        },
    ]
    const [viewMode, setViewMode] = useState<"table" | "tree">("table");
    const [topLevelTypes, setTopLevelTypes] = useState<(Type | Code)[]>([]);

    const [typeInput, setTypeInput] = useState<string>("ALL");
    const [typeFilter, setTypeFilter] = useState<string>("ALL");
    const [filterOpen, setFilterOpen] = useState(false);


    // pagination states
    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(10);
    const [dataTypeData, setDataTypeData] = useState<(Type | null)>(null);

    const { data: types, isLoading } = useDataTypes(
        pageIndex,
        pageSize,
        typeFilter === "ALL" ? undefined : typeFilter
    );

    const { data: allTypes } = useDataTypes(1, undefined, undefined);


    React.useEffect(() => {
        // setTopLevelTypes(testdata ?? [])
        setTopLevelTypes(types?.items ?? [])
        setTotalCount(types?.total ?? 0)

    }, [types])

    // Check for navigation from search
    React.useEffect(() => {
        const selectedItemStr = localStorage.getItem("cm:selectedItem");
        if (selectedItemStr) {
            try {
                const selectedItem = JSON.parse(selectedItemStr);
                console.log("Navigate to item in TreeExplorer:", selectedItem);
                // Clear the selection after handling
                localStorage.removeItem("cm:selectedItem");

                // TODO: Expand tree to show the selected item
                // This could involve expanding parent nodes and highlighting the target
            } catch (error) {
                console.error("Error parsing selected item:", error);
                localStorage.removeItem("cm:selectedItem");
            }
        }
    }, []);

    const handleCodeClick = (code: Code) => {
        console.log("Code clicked:", code);
    };

    const handleSearch = () => {
        setTypeFilter(typeInput);
    };

    const handleRefresh = (item: boolean) => {
        setTypeInput("ALL");
        setTypeFilter("ALL");
        if (item) setFilterOpen(false)
    };

    const { setBreadcrumbs } = useBreadcrumbs();

    React.useEffect(() => {
        setBreadcrumbs([
            {
                label: `DataType Manager`,
                path: `/`,
            },
        ]);
    }, []);

    console.log("some changes", dataTypeData)
    return (
        <div className=" min-h-full">
            <div className="mb-4 md:mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                    <div className="min-w-0">
                        <h2 className="text-lg md:text-xl font-semibold text-black">
                            DataType Manager
                        </h2>
                        <p className="text-black mt-1 text-sm md:text-base">
                            Manage DataTypes
                        </p>
                    </div>

                    <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
                        <div className="flex items-center bg-gray-100 rounded-lg p-1">
                            <Button
                                size="sm"
                                variant={viewMode === "table" ? "default" : "ghost"}
                                onClick={() => setViewMode("table")}
                                className="h-8 text-xs md:text-sm flex-1 sm:flex-initial"
                            >
                                <List className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                                <span className="hidden sm:inline">Table</span>
                                <span className="sm:hidden">Table</span>
                            </Button>
                            <Button
                                size="sm"
                                variant={viewMode === "tree" ? "default" : "ghost"}
                                onClick={() => setViewMode("tree")}
                                className="h-8 text-xs md:text-sm flex-1 sm:flex-initial"
                            >
                                <TreePine className="h-3 w-3 md:h-4 md:w-4 mr-1" />
                                <span className="hidden sm:inline">Tree</span>
                                <span className="sm:hidden">Tree</span>
                            </Button>
                        </div>
                        {!dataTypeData?.Name &&
                            <Button
                                className="bg-workfinity-500 hover:bg-workfinity-600 text-sm md:text-base"
                                size="sm"
                                onClick={() => setFilterOpen(true)}
                            >
                                Filter DataType
                            </Button>
                        }

                        {dataTypeData?.Name ?
                            (<AddChildDialog
                                parent={dataTypeData}
                                parentType={dataTypeData.Name ? "type" : "code"}
                                trigger={
                                    <Button
                                        size="sm"
                                        className="bg-workfinity-500 hover:bg-workfinity-600 text-sm md:text-base"
                                        onClick={(e) => e.stopPropagation()}
                                        tooltip={"Add Attribute"}
                                    >
                                        <Plus className="h-3 w-3 md:h-4 md:w-4" /> Add Attribute
                                    </Button>
                                }
                            />) : (
                                <CreateDataTypeDialog
                                    trigger={
                                        <Button
                                            className="bg-workfinity-500 hover:bg-workfinity-600 text-sm md:text-base"
                                            size="sm"
                                        >
                                            <Plus className="h-3 w-3 md:h-4 md:w-4 mr-2" />
                                            Add DataType
                                        </Button>
                                    }
                                />
                            )
                        }
                    </div>
                </div>
            </div>
            <div>
                {filterOpen &&
                    <Card className="mb-4 md:mb-6">
                        <CardContent className="pt-0">
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-black mb-2 mt-2">
                                        DataType Name
                                    </label>
                                    <Select value={typeInput} onValueChange={setTypeInput}>
                                        <SelectTrigger>
                                            <SelectValue placeholder="All types" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="ALL">All types</SelectItem>
                                            {allTypes?.items?.map((type) => (
                                                <SelectItem key={type.ID} value={type.Name!}>
                                                    {type.Name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="mt-4 flex justify-center space-x-2">
                                <Button onClick={handleSearch}>Search</Button>
                                <Button variant="outline" onClick={() => handleRefresh(false)}>
                                    Refresh
                                </Button>
                                <Button variant="outline" onClick={() => handleRefresh(true)}>
                                    Close
                                </Button>
                            </div>

                        </CardContent>
                    </Card>
                }
            </div>

            {!isLoading ?
                viewMode === "table" ? (
                    <Card>
                        <CardContent className="p-4 md:p-6">
                            <div className="space-y-2">
                                {topLevelTypes && topLevelTypes.length > 0 ? (
                                    <>
                                        <TableHierarchicalNode
                                            topLevelTypesData={topLevelTypes}
                                            itemType="type"
                                            pageIndex={pageIndex}
                                            pageSize={pageSize}
                                            totalCount={totalCount}
                                            onPageChange={setPageIndex}
                                            setDataTypeData={setDataTypeData}
                                        />

                                    </>

                                ) : (
                                    <div className="text-center py-8 text-black">
                                        <p>
                                            No DataTypes found. Create to get
                                            started.
                                        </p>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    <Card>
                        <CardContent className="p-4 md:p-6">
                            <div className="space-y-2">
                                {topLevelTypes && topLevelTypes.length > 0 ? (
                                    topLevelTypes.map((type) => (
                                        <HierarchicalNode
                                            key={type.ID}
                                            item={type}
                                            itemType="type"
                                            parentId={type?.ID}
                                            onCodeClick={handleCodeClick}
                                        />
                                    ))

                                ) : (
                                    <div className="text-center py-8 text-black">
                                        <p>
                                            No types with codes found. Create types and codes to get
                                            started.
                                        </p>
                                    </div>
                                )}
                                <PaginationControl
                                    pageIndex={pageIndex}
                                    pageSize={pageSize}
                                    totalCount={totalCount}
                                    onPageChange={setPageIndex}
                                />
                            </div>
                        </CardContent>
                    </Card>
                )
                : <div className="p-6 text-black">Loading...</div>}
        </div>

    );
}
